import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../i18n';
import Contact from './Contact';
import CookieStorage from './CookieStorage';
import LegalLanguageMenu from './LegalLanguageMenu';
import Privacy from './Privacy';
import Terms from './Terms';
import useModalEscape from './useModalEscape';

const legalItems = [
  {
    key: 'privacy',
    href: '/privacy',
    icon: 'ph ph-shield-check',
    titleKey: 'legal.privacyTitle',
    copyKey: 'legal.privacyCopy',
    statusKey: 'legal.available',
  },
  {
    key: 'terms',
    href: '/terms',
    icon: 'ph ph-file-text',
    titleKey: 'legal.termsTitle',
    copyKey: 'legal.termsCopy',
    statusKey: 'legal.available',
  },
  {
    key: 'cookies',
    href: '/cookies',
    icon: 'ph ph-hard-drives',
    titleKey: 'legal.cookiesTitle',
    copyKey: 'legal.cookiesCopy',
    statusKey: 'legal.available',
  },
  {
    key: 'contact',
    href: '/contact',
    icon: 'ph ph-envelope-simple',
    titleKey: 'legal.contactTitle',
    copyKey: 'legal.contactCopy',
    statusKey: 'legal.available',
  },
];

export default function Legal() {
  const { t } = useI18n();
  const [activeKey, setActiveKey] = useState('');
  const activeItem = useMemo(() => legalItems.find((item) => item.key === activeKey), [activeKey]);
  useModalEscape(Boolean(activeKey), () => setActiveKey(''));

  useEffect(() => {
    if (!activeKey) return undefined;
    document.body.classList.add('ct-modal-open');
    return () => {
      document.body.classList.remove('ct-modal-open');
    };
  }, [activeKey]);

  function renderDocument() {
    if (activeKey === 'privacy') return <Privacy compact />;
    if (activeKey === 'terms') return <Terms compact />;
    if (activeKey === 'cookies') return <CookieStorage compact />;
    if (activeKey === 'contact') return <Contact compact />;
    return null;
  }

  return (
    <div className="ct-legal-index-page">
      <section className="ct-legal-index-hero">
        <div className="ct-legal-index-top">
          <Link to="/" className="ct-legal-brand">
            <span className="ct-brand-mark image">
              <img src="/brand/outfit-detective-mark-transparent.svg" alt="" />
            </span>
            <span>Outfit Detective</span>
          </Link>
          <LegalLanguageMenu />
        </div>
        <p className="ct-legal-kicker">{t('legal.kicker')}</p>
        <h1>{t('legal.title')}</h1>
        <p>{t('legal.copy')}</p>
      </section>

      <section className="ct-legal-index-grid" aria-label={t('legal.title')}>
        {legalItems.map((item) => (
          <button
            type="button"
            className="ct-legal-index-card"
            key={item.key}
            onClick={() => setActiveKey(item.key)}
          >
            <i className={item.icon}></i>
            <span>{t(item.statusKey)}</span>
            <strong>{t(item.titleKey)}</strong>
            <p>{t(item.copyKey)}</p>
          </button>
        ))}
      </section>

      <footer className="ct-legal-index-footer">
        <Link to="/">{t('legal.backToApp')}</Link>
      </footer>

      {activeItem ? (
        <div className="ct-legal-modal-overlay" role="presentation" onClick={() => setActiveKey('')}>
          <section
            className="ct-legal-modal"
            role="dialog"
            aria-modal="true"
            aria-label={t(activeItem.titleKey)}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="ct-legal-modal-bar">
              <span>{t(activeItem.titleKey)}</span>
              <div className="ct-legal-modal-actions">
                <LegalLanguageMenu />
                <button type="button" onClick={() => setActiveKey('')} aria-label={t('aiDialog.close')}>
                  <i className="ph ph-x"></i>
                </button>
              </div>
            </div>
            <div className="ct-legal-modal-content">
              {renderDocument()}
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}
